import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
  Key,
  Shield,
  Calendar,
  Building2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Crown,
  RefreshCw,
  ChevronLeft,
  Package,
  Lock,
  ExternalLink
} from 'lucide-react';
import { GET_LICENCIA_SISTEMA, ACTIVAR_LICENCIA, GET_MODULOS_DISPONIBLES } from '../graphql/licencia';
import toast from 'react-hot-toast';
import './MiLicencia.css';

// URL del gestor de licencias (desde variable de entorno)
const LICENSE_API_URL = import.meta.env.VITE_LICENSE_API_URL || 'http://localhost:4003/api/v1';

function MiLicencia() {
  const navigate = useNavigate();

  // Estado local
  const [codigoInput, setCodigoInput] = useState('');
  const [cargando, setCargando] = useState(false);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);

  // Query GraphQL - Licencia
  const { data, loading, error, refetch } = useQuery(GET_LICENCIA_SISTEMA, {
    fetchPolicy: 'network-only'
  });

  // Query GraphQL - Modulos
  const { data: modulosData } = useQuery(GET_MODULOS_DISPONIBLES, {
    fetchPolicy: 'cache-first'
  });

  // Mutation GraphQL
  const [activarLicenciaMutation] = useMutation(ACTIVAR_LICENCIA);

  const licencia = data?.licenciaSistema;
  const todosModulos = modulosData?.modulosDisponibles || [];

  // Separar modulos core y premium
  const modulosCore = todosModulos.filter(m => m.core);
  const modulosPremium = todosModulos.filter(m => !m.core);
  const modulosPremiumActivos = modulosPremium.filter(m => m.habilitado);

  // Determinar estado visual
  const getEstadoVisual = () => {
    if (!licencia) {
      return { color: 'gris', icono: <Key size={48} />, texto: 'CARGANDO' };
    }

    if (licencia.estado_licencia === 'suspendida') {
      return { color: 'rojo', icono: <XCircle size={48} />, texto: 'SUSPENDIDA' };
    }

    if (licencia.permanente) {
      return { color: 'azul', icono: <Crown size={48} />, texto: 'PERMANENTE' };
    }

    if (licencia.estado_licencia === 'sin_activar') {
      return { color: 'gris', icono: <Key size={48} />, texto: 'SIN ACTIVAR' };
    }

    if (licencia.estado_licencia === 'vencida') {
      return { color: 'rojo', icono: <XCircle size={48} />, texto: 'VENCIDA' };
    }

    if (licencia.en_gracia) {
      return { color: 'naranja', icono: <AlertTriangle size={48} />, texto: 'EN GRACIA' };
    }

    if (licencia.dias_restantes !== null && licencia.dias_restantes <= 7) {
      return { color: 'naranja', icono: <AlertTriangle size={48} />, texto: 'POR VENCER' };
    }

    if (licencia.dias_restantes !== null && licencia.dias_restantes <= 30) {
      return { color: 'amarillo', icono: <Calendar size={48} />, texto: 'ACTIVA' };
    }

    return { color: 'verde', icono: <CheckCircle size={48} />, texto: 'ACTIVA' };
  };

  const estadoVisual = getEstadoVisual();

  // Obtener info del comercio por codigo desde el gestor
  const obtenerInfoCodigo = async (codigo) => {
    try {
      const response = await fetch(`${LICENSE_API_URL}/info-codigo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codigo })
      });
      return await response.json();
    } catch (error) {
      console.error('Error obteniendo info:', error);
      return { success: false, message: 'Error de conexion con el servidor de licencias' };
    }
  };

  // Activar licencia
  const handleActivar = async () => {
    if (!codigoInput || codigoInput.length !== 32) {
      toast.error('El codigo debe tener 32 caracteres');
      return;
    }

    setCargando(true);

    try {
      // 1. Obtener info del comercio desde el gestor
      const infoResult = await obtenerInfoCodigo(codigoInput);

      if (!infoResult.success) {
        toast.error(infoResult.message || 'Codigo invalido');
        setCargando(false);
        return;
      }

      const { comercio, licencia: licenciaInfo } = infoResult;

      // 2. Activar en el gestor y obtener huella
      const activarResponse = await fetch(`${LICENSE_API_URL}/activar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          codigo: codigoInput,
          nit: comercio.nit,
          nombre_comercial: comercio.nombre_comercial,
          razon_social: comercio.razon_social
        })
      });

      const activarData = await activarResponse.json();

      if (!activarData.success) {
        toast.error(activarData.message || 'Error al activar');
        setCargando(false);
        return;
      }

      // 3. Guardar en LocalStorage
      localStorage.setItem('licencia_codigo', codigoInput.toUpperCase());
      localStorage.setItem('licencia_huella', activarData.huella);
      localStorage.setItem('licencia_tipo', activarData.tipo_licencia);
      localStorage.setItem('licencia_vencimiento', activarData.fecha_vencimiento || '');
      localStorage.setItem('licencia_estado', 'activa');
      localStorage.setItem('comercio_nombre', comercio.nombre_comercial);
      localStorage.setItem('comercio_nit', comercio.nit);
      localStorage.setItem('comercio_razon_social', comercio.razon_social);
      localStorage.setItem('comercio_ciudad', comercio.ciudad || '');
      localStorage.setItem('licencia_fecha_activacion', new Date().toISOString().split('T')[0]);

      if (activarData.modulos) {
        localStorage.setItem('licencia_modulos', JSON.stringify(activarData.modulos));
        localStorage.setItem('licencia_modulos_version', activarData.modulos_version || '');
      }

      // 4. Guardar en BD via GraphQL
      const result = await activarLicenciaMutation({
        variables: {
          input: {
            codigo: codigoInput.toUpperCase(),
            nit: comercio.nit,
            nombre_comercial: comercio.nombre_comercial,
            razon_social: comercio.razon_social,
            ciudad: comercio.ciudad || '',
            huella: activarData.huella,
            fecha_vencimiento: activarData.fecha_vencimiento || null,
            tipo_licencia: activarData.tipo_licencia,
            modulos: activarData.modulos || [],
            modulos_version: activarData.modulos_version != null ? String(activarData.modulos_version) : null
          }
        }
      });

      if (result.data?.activarLicenciaSistema?.success) {
        toast.success('Licencia activada exitosamente');
        setMostrarFormulario(false);
        setCodigoInput('');
        refetch();
      } else {
        toast.error(result.data?.activarLicenciaSistema?.mensaje || 'Error al guardar');
      }

    } catch (error) {
      console.error('Error en activacion:', error);
      toast.error('Error de conexion. Intente nuevamente.');
    } finally {
      setCargando(false);
    }
  };

  // Sincronizar modulos con el servidor
  const handleSincronizar = async () => {
    if (!licencia?.codigo_licencia || !licencia?.nit) {
      toast.error('No hay licencia para sincronizar');
      return;
    }

    setSincronizando(true);

    try {
      const response = await fetch(`${LICENSE_API_URL}/estado`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          codigo: licencia.codigo_licencia,
          nit: licencia.nit,
          modulos_version: licencia.modulos_version || ''
        })
      });

      const data = await response.json();

      if (data.success) {
        // Actualizar estado si cambio
        if (data.estado === 'suspendida') {
          localStorage.setItem('licencia_estado', 'suspendida');
        }

        // Actualizar modulos si hay cambios
        if (data.modulos_actualizados && data.modulos) {
          localStorage.setItem('licencia_modulos', JSON.stringify(data.modulos));
          localStorage.setItem('licencia_modulos_version', data.modulos_version);
        }

        toast.success('Sincronizacion completada');
        refetch();
      } else {
        toast.error(data.message || 'Error al sincronizar');
      }
    } catch (error) {
      console.error('Error sincronizando:', error);
      toast.error('Error de conexion');
    } finally {
      setSincronizando(false);
    }
  };

  // Formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Contactar soporte
  const handleContactarSoporte = () => {
    window.open('https://wa.me/573001234567?text=Hola,%20necesito%20información%20sobre%20módulos%20premium%20para%20mi%20licencia', '_blank');
  };

  if (loading) {
    return (
      <div className="mi-licencia">
        <div className="mi-licencia__loading">
          <RefreshCw className="spin" size={32} />
          <p>Cargando informacion de licencia...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mi-licencia">
        <div className="mi-licencia__error">
          <XCircle size={48} />
          <p>Error al cargar la licencia</p>
          <button onClick={() => refetch()}>Reintentar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="mi-licencia">
      {/* Header */}
      <div className="mi-licencia__header">
        <button className="mi-licencia__back" onClick={() => navigate(-1)}>
          <ChevronLeft size={20} />
          Volver
        </button>
        <h1>
          <Key size={28} />
          Mi Licencia
        </h1>
      </div>

      {/* Estado de la Licencia */}
      <div className={`mi-licencia__estado estado--${estadoVisual.color}`}>
        <div className="mi-licencia__estado-icono">
          {estadoVisual.icono}
        </div>
        <div className="mi-licencia__estado-info">
          <h2>Estado: {estadoVisual.texto}</h2>
          {licencia?.permanente ? (
            <p>Licencia permanente sin fecha de vencimiento</p>
          ) : licencia?.dias_restantes !== null ? (
            licencia.dias_restantes > 0 ? (
              <p>{licencia.dias_restantes} dias restantes</p>
            ) : licencia.en_gracia ? (
              <p>En periodo de gracia: {licencia.dias_gracia + licencia.dias_restantes} dias restantes</p>
            ) : (
              <p>Licencia vencida</p>
            )
          ) : (
            <p>{licencia?.mensaje || 'Sin licencia activada'}</p>
          )}
        </div>
      </div>

      {/* Informacion del Comercio */}
      <div className="mi-licencia__seccion">
        <h3>
          <Building2 size={20} />
          Informacion del Comercio
        </h3>
        <div className="mi-licencia__grid">
          <div className="mi-licencia__campo">
            <label>NIT:</label>
            <span>{licencia?.nit || '-'}</span>
          </div>
          <div className="mi-licencia__campo">
            <label>Nombre Comercial:</label>
            <span>{licencia?.nombre_comercial || '-'}</span>
          </div>
          <div className="mi-licencia__campo">
            <label>Razon Social:</label>
            <span>{licencia?.razon_social || '-'}</span>
          </div>
          <div className="mi-licencia__campo">
            <label>Ciudad:</label>
            <span>{licencia?.ciudad || '-'}</span>
          </div>
        </div>
      </div>

      {/* Detalles de la Licencia */}
      <div className="mi-licencia__seccion">
        <h3>
          <Shield size={20} />
          Detalles de la Licencia
        </h3>
        <div className="mi-licencia__grid">
          <div className="mi-licencia__campo">
            <label>Codigo:</label>
            <span className="mi-licencia__codigo">
              {licencia?.codigo_licencia
                ? `${licencia.codigo_licencia.substring(0, 8)}...${licencia.codigo_licencia.substring(24)}`
                : '-'}
            </span>
          </div>
          <div className="mi-licencia__campo">
            <label>Tipo:</label>
            <span>{licencia?.tipo_licencia_nombre || '-'}</span>
          </div>
          <div className="mi-licencia__campo">
            <label>Fecha de Activacion:</label>
            <span>{formatearFecha(licencia?.fecha_activacion)}</span>
          </div>
          <div className="mi-licencia__campo">
            <label>Fecha de Vencimiento:</label>
            <span>
              {licencia?.permanente
                ? 'Permanente (sin vencimiento)'
                : formatearFecha(licencia?.fecha_vencimiento)}
            </span>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* SECCION DE MODULOS INCLUIDOS */}
      {/* ============================================ */}
      <div className="mi-licencia__modulos-seccion mi-licencia__modulos-seccion--incluidos">
        <div className="mi-licencia__modulos-header">
          <div className="mi-licencia__modulos-titulo">
            <Package size={24} />
            <h3>Modulos Incluidos</h3>
          </div>
          <span className="mi-licencia__modulos-contador mi-licencia__modulos-contador--verde">
            {modulosCore.length} modulos
          </span>
        </div>
        <p className="mi-licencia__modulos-subtitulo">
          Estos modulos estan incluidos en tu licencia y siempre estaran disponibles.
        </p>
        <div className="mi-licencia__modulos-grid">
          {modulosCore.map(modulo => (
            <div key={modulo.codigo} className="mi-licencia__modulo-card mi-licencia__modulo-card--core">
              <div className="mi-licencia__modulo-icono mi-licencia__modulo-icono--verde">
                <CheckCircle size={20} />
              </div>
              <div className="mi-licencia__modulo-info">
                <span className="mi-licencia__modulo-nombre">{modulo.nombre}</span>
                <span className="mi-licencia__modulo-estado mi-licencia__modulo-estado--incluido">
                  Incluido
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ============================================ */}
      {/* SECCION DE MODULOS PREMIUM */}
      {/* ============================================ */}
      <div className="mi-licencia__modulos-seccion mi-licencia__modulos-seccion--premium">
        <div className="mi-licencia__modulos-header">
          <div className="mi-licencia__modulos-titulo">
            <Crown size={24} />
            <h3>Modulos Premium</h3>
          </div>
          <span className="mi-licencia__modulos-contador mi-licencia__modulos-contador--amarillo">
            {modulosPremiumActivos.length} de {modulosPremium.length} activos
          </span>
        </div>
        <p className="mi-licencia__modulos-subtitulo">
          Estos modulos requieren activacion adicional. Contacta a soporte para habilitarlos.
        </p>
        <div className="mi-licencia__modulos-grid">
          {modulosPremium.map(modulo => (
            <div
              key={modulo.codigo}
              className={`mi-licencia__modulo-card mi-licencia__modulo-card--premium ${
                modulo.habilitado ? 'mi-licencia__modulo-card--activo' : ''
              }`}
            >
              <span className="mi-licencia__modulo-badge">
                <Crown size={12} />
                PREMIUM
              </span>
              <div className="mi-licencia__modulo-icono mi-licencia__modulo-icono--gris">
                {modulo.habilitado ? <CheckCircle size={20} /> : <Lock size={20} />}
              </div>
              <div className="mi-licencia__modulo-info">
                <span className="mi-licencia__modulo-nombre">{modulo.nombre}</span>
                <span className={`mi-licencia__modulo-estado ${
                  modulo.habilitado
                    ? 'mi-licencia__modulo-estado--activo'
                    : 'mi-licencia__modulo-estado--inactivo'
                }`}>
                  {modulo.habilitado ? 'Activo' : 'No incluido'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ============================================ */}
      {/* SECCION NECESITAS MAS MODULOS */}
      {/* ============================================ */}
      <div className="mi-licencia__cta-seccion">
        <h3>Necesitas mas modulos?</h3>
        <p>
          Contacta a nuestro equipo de soporte para actualizar tu licencia y desbloquear
          funcionalidades adicionales como WhatsApp Business, Chatbot IA, Nomina Electronica y mas.
        </p>
        <button className="mi-licencia__cta-btn" onClick={handleContactarSoporte}>
          <ExternalLink size={18} />
          Contactar Soporte
        </button>
      </div>

      {/* Acciones */}
      <div className="mi-licencia__acciones">
        {!licencia?.codigo_licencia || licencia?.estado_licencia === 'sin_activar' ? (
          // Sin licencia - mostrar formulario de activacion
          <div className="mi-licencia__activacion">
            {!mostrarFormulario ? (
              <button
                className="mi-licencia__btn mi-licencia__btn--primary"
                onClick={() => setMostrarFormulario(true)}
              >
                <Key size={20} />
                Activar Licencia
              </button>
            ) : (
              <div className="mi-licencia__formulario">
                <h4>Ingrese el codigo de licencia</h4>
                <input
                  type="text"
                  value={codigoInput}
                  onChange={(e) => setCodigoInput(e.target.value.toUpperCase().replace(/[^A-F0-9]/gi, ''))}
                  placeholder="Codigo de 32 caracteres"
                  maxLength={32}
                  className="mi-licencia__input"
                  disabled={cargando}
                />
                <p className="mi-licencia__contador">{codigoInput.length}/32 caracteres</p>
                <div className="mi-licencia__botones">
                  <button
                    className="mi-licencia__btn mi-licencia__btn--primary"
                    onClick={handleActivar}
                    disabled={cargando || codigoInput.length !== 32}
                  >
                    {cargando ? (
                      <>
                        <RefreshCw className="spin" size={18} />
                        Activando...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        Activar
                      </>
                    )}
                  </button>
                  <button
                    className="mi-licencia__btn mi-licencia__btn--secondary"
                    onClick={() => {
                      setMostrarFormulario(false);
                      setCodigoInput('');
                    }}
                    disabled={cargando}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Con licencia - mostrar boton de sincronizar y renovar
          <div className="mi-licencia__activacion">
            {!mostrarFormulario ? (
              <div className="mi-licencia__botones">
                <button
                  className="mi-licencia__btn mi-licencia__btn--secondary"
                  onClick={handleSincronizar}
                  disabled={sincronizando}
                >
                  {sincronizando ? (
                    <>
                      <RefreshCw className="spin" size={18} />
                      Sincronizando...
                    </>
                  ) : (
                    <>
                      <RefreshCw size={18} />
                      Sincronizar con Servidor
                    </>
                  )}
                </button>
                <button
                  className="mi-licencia__btn mi-licencia__btn--primary"
                  onClick={() => setMostrarFormulario(true)}
                >
                  <Key size={18} />
                  Renovar Licencia
                </button>
              </div>
            ) : (
              <div className="mi-licencia__formulario">
                <h4>Ingrese el nuevo codigo de licencia</h4>
                <input
                  type="text"
                  value={codigoInput}
                  onChange={(e) => setCodigoInput(e.target.value.toUpperCase().replace(/[^A-F0-9]/gi, ''))}
                  placeholder="Codigo de 32 caracteres"
                  maxLength={32}
                  className="mi-licencia__input"
                  disabled={cargando}
                />
                <p className="mi-licencia__contador">{codigoInput.length}/32 caracteres</p>
                <div className="mi-licencia__botones">
                  <button
                    className="mi-licencia__btn mi-licencia__btn--primary"
                    onClick={handleActivar}
                    disabled={cargando || codigoInput.length !== 32}
                  >
                    {cargando ? (
                      <>
                        <RefreshCw className="spin" size={18} />
                        Renovando...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        Renovar
                      </>
                    )}
                  </button>
                  <button
                    className="mi-licencia__btn mi-licencia__btn--secondary"
                    onClick={() => {
                      setMostrarFormulario(false);
                      setCodigoInput('');
                    }}
                    disabled={cargando}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default MiLicencia;
